<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * action_params shape:
 *   [{ inbox_id: Number, body: String }]
 */
const props = defineProps({
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const allInboxes = useMapGetter('inboxes/getInboxes');

const value = computed(() => {
  if (Array.isArray(props.modelValue) && props.modelValue[0]) {
    return props.modelValue[0];
  }
  return {};
});

const liquidHints = [
  '{{contact.name}}',
  '{{contact.phone_number}}',
  '{{conversation.id}}',
];

function update(patch) {
  emit('update:modelValue', [{ ...value.value, ...patch }]);
}

const inboxId = computed({
  get: () => value.value.inbox_id || null,
  set: id => update({ inbox_id: id }),
});

const fallbackInboxId = computed({
  get: () => value.value.fallback_inbox_id || null,
  set: id => update({ fallback_inbox_id: id || null }),
});

const fallbackOptions = computed(() => {
  const messagingTypes = [
    'Channel::Whatsapp',
    'Channel::Email',
    'Channel::Sms',
    'Channel::TwilioSms',
  ];
  return (allInboxes.value || []).filter(i => {
    if (i.id === inboxId.value) return false;
    if (!messagingTypes.includes(i.channel_type)) return false;
    if (i.channel_type === 'Channel::TwilioSms' && i.medium !== 'sms')
      return false;
    return true;
  });
});

function fallbackOptionLabel(inbox) {
  const map = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::Email': 'Email',
    'Channel::Sms': 'SMS',
    'Channel::TwilioSms': 'SMS',
  };
  const channel = map[inbox.channel_type] || inbox.channel_type;
  return `${inbox.name} — ${channel}`;
}

const body = computed({
  get: () => value.value.body || '',
  set: v => update({ body: v }),
});
</script>

<template>
  <div class="w-full mt-2 flex flex-col gap-2">
    <div>
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.SMS_MESSAGE.INBOX_LABEL') }}
      </label>
      <select v-model="inboxId" class="m-0 w-full">
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.SMS_MESSAGE.INBOX_PLACEHOLDER') }}
        </option>
        <option v-for="inbox in smsInboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>
    <div>
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.SMS_MESSAGE.BODY_LABEL') }}
      </label>
      <textarea
        v-model="body"
        rows="3"
        class="answer--text-input mb-0 w-full"
        :placeholder="t('AUTOMATION.ACTION.SMS_MESSAGE.BODY_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('AUTOMATION.ACTION.SMS_MESSAGE.LIQUID_HINT') }}
        <code
          v-for="hint in liquidHints"
          :key="hint"
          class="text-xs px-1 mx-0.5 bg-n-alpha-2 rounded"
        >
          {{ hint }}
        </code>
      </p>
    </div>
    <div v-if="inboxId">
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.LABEL') }}
      </label>
      <select v-model="fallbackInboxId" class="m-0 w-full">
        <option :value="null">
          {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.PLACEHOLDER') }}
        </option>
        <option
          v-for="inbox in fallbackOptions"
          :key="inbox.id"
          :value="inbox.id"
        >
          {{ fallbackOptionLabel(inbox) }}
        </option>
      </select>
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.HINT') }}
      </p>
    </div>
  </div>
</template>
